import { useMemo, useRef } from 'react'

import { UseFormReturn, useWatch } from 'react-hook-form'

import { findBankAccountMatch } from '~/components/forms/transfer/hooks/useOfframpDisplayData'
import { TransferFormValues } from '~/components/forms/transfer/transferSchema'
import { EURC_TOKENS, MINIMUM_OFFRAMP_AMOUNT } from '~/constants/addresses'
import { useRequiredActiveOrg } from '~/hooks/org'
import { useSyncExternalValue } from '~/hooks/useSyncExternalValue'

/**
 * Syncs whether the offramp amount is below the $10/€10 minimum into the
 * form so the schema refinement can gate submission. The inline field error
 * is produced by transferFormResolver, not here.
 *
 * The minimum applies to the fiat the bank receives, which the user types
 * directly into `offrampRecipientAmount`, so no token pricing is involved.
 */
export function useIsBelowMinimumOfframpAmount({
  methods,
}: {
  methods: UseFormReturn<TransferFormValues>
}) {
  const { control } = methods
  const recipients = useWatch({ control, name: 'recipients' })
  const transferToken = useWatch({ control, name: 'transferToken' })
  const offrampRecipientAmount = useWatch({
    control,
    name: 'offrampRecipientAmount',
  })

  const org = useRequiredActiveOrg()

  const isBankAccount = useMemo(() => {
    return recipients.some(
      (recipient) =>
        !!findBankAccountMatch(
          org?.bridge?.externalAccounts,
          recipient.receiver?.address,
        ),
    )
  }, [recipients, org])

  const isEurc =
    !!transferToken?.chainId &&
    transferToken?.token?.toLowerCase() ===
      EURC_TOKENS[transferToken.chainId]?.toLowerCase()

  // undefined while the field is empty, so a cleared input doesn't flap the
  // gate on and off
  const isUnder10Determination = useMemo<boolean | undefined>(() => {
    if (!offrampRecipientAmount) return undefined
    const amount = Number(offrampRecipientAmount)
    if (Number.isNaN(amount)) return false
    return amount < MINIMUM_OFFRAMP_AMOUNT
  }, [offrampRecipientAmount])

  // Hold the last determinable answer so loading windows don't flicker
  const lastDeterminationRef = useRef(false)
  if (isUnder10Determination !== undefined) {
    lastDeterminationRef.current = isUnder10Determination
  }
  const isUnder10 = lastDeterminationRef.current

  useSyncExternalValue({
    methods,
    field: 'offrampBelowMinimum',
    value: isBankAccount && isUnder10,
    revalidates: 'offrampRecipientAmount',
  })

  useSyncExternalValue({
    methods,
    field: 'offrampIsEurc',
    value: isEurc,
    revalidates: 'offrampRecipientAmount',
  })
}
